/**
 * Internationalisation setup.
 *
 * The locale is chosen once at startup — persisted choice first, then the
 * browser language — and can be switched at runtime from the navigation rail.
 * Switching is instant (no reload): every string in the templates goes through
 * the reactive `t()`/`d()`/`n()` API.
 *
 * Adding a language = one new file in ./locales plus one entry in
 * SUPPORT_LOCALES and `messages` below. The French catalogue is typed against
 * the English one, so incomplete catalogues fail the build.
 */
import { createI18n } from 'vue-i18n';
import en from './locales/en';
import fr from './locales/fr';

export const SUPPORT_LOCALES = ['en', 'fr'] as const;
export type AppLocale = (typeof SUPPORT_LOCALES)[number];

const STORAGE_KEY = 'braim.locale';

function isAppLocale(value: unknown): value is AppLocale {
  return (SUPPORT_LOCALES as readonly string[]).includes(value as string);
}

/** Persisted choice first; otherwise the browser language; English fallback. */
function detectLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isAppLocale(stored)) return stored;
  } catch {
    // Storage can be unavailable (private mode with strict settings) — fall through.
  }
  return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

/**
 * Datetime formats mirror what the previous `toLocaleString` family produced,
 * but follow the SELECTED locale rather than the browser one.
 */
const datetimeFormats = {
  date: { year: 'numeric', month: 'numeric', day: 'numeric' },
  datetime: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  },
  time: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
  month: { month: 'short', year: 'numeric' },
} as const;

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: { en, fr },
  datetimeFormats: { en: datetimeFormats, fr: datetimeFormats },
});

/** Switches the app language, persists it, and keeps the document in step. */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not persisting is fine; the session still switches.
  }
  document.documentElement.lang = locale;
}

document.documentElement.lang = i18n.global.locale.value;

export default i18n;
